import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ReportRequest, ReportType, TipoRelatorio } from '../../../interface';
import Pagination from './Pagination';
import StatusPill from './StatusPill';

const PER_PAGE = 10;

const Card = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: ${(props) => props.theme.border.default};
  box-shadow: ${(props) => props.theme.shadow.default};
  padding: 1.25rem 1.5rem;
  margin: 0 0 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-sizing: border-box;
  width: 100%;
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Title = styled.h2`
  font-size: ${(props) => props.theme.fontSize.big};
  color: ${(props) => props.theme.colors.mainColor};
  font-weight: 700;
  margin: 0;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const RefreshButton = styled.button<{ $spinning: boolean }>`
  background: transparent;
  border: 1px solid ${(props) => props.theme.colors.mainColor};
  color: ${(props) => props.theme.colors.mainColor};
  border-radius: ${(props) => props.theme.border.default};
  padding: 0.35rem 0.75rem;
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: all ${(props) => props.theme.transitionDuration.default};

  & svg {
    font-size: 1rem;
    animation: ${(props) => (props.$spinning ? spin : 'none')} 1s linear
      infinite;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.mainColor};
    color: ${(props) => props.theme.colors.white};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background-color: ${(props) => props.theme.colors.background};
  }

  th {
    text-align: left;
    padding: 0.75rem 1rem;
    font-size: ${(props) => props.theme.fontSize.small};
    color: ${(props) => props.theme.colors.lightGray};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #eaeaea;
  }

  td {
    padding: 0.75rem 1rem;
    font-size: ${(props) => props.theme.fontSize.medium};
    color: ${(props) => props.theme.colors.gray};
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
  }

  tbody tr:hover {
    background-color: ${(props) => props.theme.colors.background};
  }
`;

const DownloadButton = styled.button`
  background-color: ${(props) => props.theme.colors.mainColor};
  color: ${(props) => props.theme.colors.white};
  border: none;
  border-radius: ${(props) => props.theme.border.default};
  padding: 0.4rem 0.9rem;
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: background-color
    ${(props) => props.theme.transitionDuration.default};

  & svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.hover};
  }

  &:disabled {
    background-color: ${(props) => props.theme.colors.desactivated};
    cursor: not-allowed;
    opacity: 0.7;

    &:hover {
      background-color: ${(props) => props.theme.colors.desactivated};
    }
  }
`;

const Empty = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${(props) => props.theme.colors.lightGray};
  font-size: ${(props) => props.theme.fontSize.medium};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Counter = styled.span`
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.lightGray};
`;

const Protocolo = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: ${(props) => props.theme.colors.mainColor};
  white-space: nowrap;
`;

interface TipoBadgeProps {
  $tarifas: boolean;
}

// Pinta o tipo de relatorio com cores distintas pra leitura rapida na
// listagem unificada: Tarifas Gerais (principal) em destaque, Recebimento
// em tom neutro.
const TipoBadge = styled.span<TipoBadgeProps>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 1rem;
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 600;
  white-space: nowrap;
  background-color: ${(props) =>
    props.$tarifas
      ? props.theme.colors.mainColor
      : props.theme.colors.background};
  color: ${(props) =>
    props.$tarifas ? props.theme.colors.white : props.theme.colors.gray};
`;

function formatBR(date: string): string {
  if (!date) return '—';
  const parts = date.split('-');
  if (parts.length !== 3) return date;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const date = `${String(d.getDate()).padStart(2, '0')}/${String(
    d.getMonth() + 1
  ).padStart(2, '0')}/${d.getFullYear()}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
  return `${date} ${time}`;
}

function typeLabel(t: ReportType): string {
  return t === 'analitico' ? 'Analítico' : 'Sintético';
}

function tipoRelatorioLabel(t: TipoRelatorio): string {
  return t === 'tarifas' ? 'Tarifas Gerais' : 'Tarifas de Recebimento';
}

interface Props {
  reports: ReportRequest[];
  refreshing: boolean;
  onRefresh: () => void;
  // eslint-disable-next-line no-unused-vars
  onDownload: (id: string) => void;
}

function RequestsTable({ reports, refreshing, onRefresh, onDownload }: Props) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(Math.ceil(reports.length / PER_PAGE), 1);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const visibleReports = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return reports.slice(start, start + PER_PAGE);
  }, [reports, currentPage]);

  return (
    <Card>
      <HeaderBar>
        <Title>Solicitações</Title>
        <RefreshButton
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          $spinning={refreshing}
          title="Atualizar lista">
          <RefreshIcon fontSize="small" />
          {refreshing ? 'Atualizando…' : 'Atualizar'}
        </RefreshButton>
      </HeaderBar>
      {reports.length === 0 ? (
        <Empty>
          Nenhuma solicitação ainda. Use o formulário acima para gerar a
          primeira.
        </Empty>
      ) : (
        <>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Tipo de Relatório</th>
                  <th>Formato</th>
                  <th>Parceiro</th>
                  <th>Período</th>
                  <th>Solicitado por</th>
                  <th>Atualizado em</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visibleReports.map((r) => {
                  const downloadEnabled = r.status === 'ready';
                  return (
                    <tr key={r.id}>
                      <td>
                        <Protocolo>#{r.id}</Protocolo>
                      </td>
                      <td>
                        <TipoBadge $tarifas={r.tipoRelatorio === 'tarifas'}>
                          {tipoRelatorioLabel(r.tipoRelatorio)}
                        </TipoBadge>
                      </td>
                      <td>{typeLabel(r.type)}</td>
                      <td>{r.partnerName}</td>
                      <td>
                        {formatBR(r.dateStart)} a {formatBR(r.dateEnd)}
                      </td>
                      <td>{r.requestedBy}</td>
                      <td>{formatDateTime(r.updatedAt || r.createdAt)}</td>
                      <td>
                        <StatusPill status={r.status} />
                      </td>
                      <td>
                        <DownloadButton
                          type="button"
                          onClick={() => onDownload(r.id)}
                          disabled={!downloadEnabled}
                          title={
                            downloadEnabled
                              ? 'Baixar CSV'
                              : 'Disponível quando o relatório for concluído'
                          }>
                          <GetAppIcon fontSize="small" />
                          Baixar CSV
                        </DownloadButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
          <Footer>
            <Counter>
              {reports.length}{' '}
              {reports.length === 1 ? 'solicitação' : 'solicitações'}
              {totalPages > 1 && (
                <>
                  {' '}
                  · Página {currentPage} de {totalPages}
                </>
              )}
            </Counter>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={setCurrentPage}
            />
          </Footer>
        </>
      )}
    </Card>
  );
}

export default RequestsTable;
